/**
 * "MCOS treatment plans": every decision the clinic has made.
 *
 * Source: the clinic dashboard visual of 17 September 2026, which has this as
 * its own sidebar entry alongside New Patients.
 *
 * The distinction between this and the work queue is the whole reason both
 * exist. New patients is who has NOT been decided about, and it empties as the
 * clinic works. This is the record of what WAS decided, and it only grows. A
 * single screen trying to be both would either hide finished plans, which
 * makes it useless as a record, or keep them, which makes it useless as a
 * queue.
 *
 * Drafts appear here and are labelled. A draft is a decision half made, and
 * the clinician who saved it is the only person who will ever remember it
 * exists unless it is on a list.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  Card,
  EmptyState,
  ErrorState,
  LoadingRows,
  PageHeader,
  Pill,
  Table,
  Td,
  Th,
  inputClass,
} from '../components/ui';

interface PlanRow {
  id: string;
  regimenCode: string;
  status: 'DRAFT' | 'ACTIVE' | 'STOPPED';
  combinationKey: string;
  selectedAt: string;
  startedAt: string | null;
  stoppedAt: string | null;
  stopReason: string | null;
  goalSnapshot: { primaryGoal?: string | null; targetValue?: number | null; targetUnit?: string | null } | null;
  twinEvidenceSnapshot: { cohortSize?: number; sufficient?: boolean; goalAttainment?: { rate?: number | null } } | null;
  patient: { id: string; firstName: string; lastName: string };
  treatments: Array<{ dose: string | null; frequency: string | null; treatment: { name: string; category: string } }>;
}

interface PlanPage {
  total: number;
  page: number;
  pageSize: number;
  plans: PlanRow[];
}

const STATUS_CLASS: Record<PlanRow['status'], string> = {
  DRAFT: 'bg-ink/5 text-ink-muted',
  ACTIVE: 'bg-[var(--sage-soft)] text-sage-deep',
  STOPPED: 'bg-[var(--amber-soft)] text-amber-deep',
};

function goalOf(plan: PlanRow): string {
  const goal = plan.goalSnapshot?.primaryGoal;
  if (!goal) return 'No goal recorded';
  const head = goal.charAt(0).toUpperCase() + goal.slice(1);
  const value = plan.goalSnapshot?.targetValue;
  if (value === null || value === undefined) return head;
  return `${head}, ${value}${plan.goalSnapshot?.targetUnit ? ` ${plan.goalSnapshot.targetUnit}` : ''}`;
}

function formatDate(value: string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function TreatmentPlansPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<'' | PlanRow['status']>('');
  const [query, setQuery] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    // Keyed on every filter, so an in-flight request for the old filter can
    // never land after the new one and paint the wrong rows.
    queryKey: ['treatment-plans', page, status, query],
    queryFn: () =>
      api.get<PlanPage>(
        `/regimens/plans?page=${page}&pageSize=25${status ? `&status=${status}` : ''}${
          query ? `&q=${encodeURIComponent(query)}` : ''
        }`,
      ),
  });

  if (error) return <ErrorState error={error} retry={() => void refetch()} />;

  const plans = data?.plans ?? [];
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title="MCOS treatment plans"
        subtitle={
          data ? `${data.total} ${data.total === 1 ? 'plan' : 'plans'} on record` : 'Every treatment decision this clinic has made.'
        }
      />

      <Card className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            className={`${inputClass} max-w-xs`}
            placeholder="Search by patient name"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
          />
          {/* Chips, gathered on one side, and visually distinct from the
              status badges in the rows: a control must never look like data. */}
          <div className="flex gap-1.5">
            {(['', 'ACTIVE', 'DRAFT', 'STOPPED'] as const).map((option) => (
              <button
                key={option || 'all'}
                type="button"
                onClick={() => {
                  setStatus(option);
                  setPage(1);
                }}
                className={`rounded-control border px-3 py-1.5 text-xs font-semibold transition ${
                  status === option
                    ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)] text-white'
                    : 'border-ink/15 text-ink-muted hover:text-ink'
                }`}
              >
                {option === '' ? 'All' : option.charAt(0) + option.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <div className="p-5">
            <LoadingRows rows={6} />
          </div>
        ) : plans.length === 0 ? (
          <EmptyState
            title="No treatment plans yet"
            body="A plan appears here the moment a clinician releases one from a patient's treatment panel."
          />
        ) : (
          <Table minWidth={760}>
            <thead>
              <tr>
                <Th>Patient</Th>
                <Th>Goal</Th>
                <Th>Treatments</Th>
                <Th>Evidence</Th>
                <Th>Started</Th>
                <Th align="right">Status</Th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id} className="hover:bg-ink/[0.02]">
                  <Td>
                    <Link to={`/patients/${plan.patient.id}`} className="font-semibold text-ink hover:underline">
                      {plan.patient.firstName} {plan.patient.lastName}
                    </Link>
                    <span className="block text-xs text-ink-muted">{plan.regimenCode}</span>
                  </Td>
                  <Td className="text-ink-muted">{goalOf(plan)}</Td>
                  <Td>
                    <span className="text-ink">
                      {plan.treatments.map((row) => row.treatment.name).join(' + ') || '-'}
                    </span>
                    {plan.treatments.some((row) => row.dose) && (
                      <span className="block text-xs text-ink-muted">
                        {plan.treatments
                          .filter((row) => row.dose)
                          .map((row) => `${row.treatment.name} ${row.dose}`)
                          .join(', ')}
                      </span>
                    )}
                  </Td>
                  <Td className="text-ink-muted">
                    {/* What the clinician was shown when they chose, not what
                        the cohort looks like today. */}
                    {plan.twinEvidenceSnapshot?.sufficient
                      ? `${plan.twinEvidenceSnapshot.cohortSize} matched${
                          plan.twinEvidenceSnapshot.goalAttainment?.rate !== null &&
                          plan.twinEvidenceSnapshot.goalAttainment?.rate !== undefined
                            ? `, ${plan.twinEvidenceSnapshot.goalAttainment.rate}%`
                            : ''
                        }`
                      : 'Limited MCOS data'}
                  </Td>
                  <Td className="text-ink-muted">{formatDate(plan.startedAt ?? plan.selectedAt)}</Td>
                  <Td align="right">
                    <Pill className={STATUS_CLASS[plan.status]}>
                      {plan.status.charAt(0) + plan.status.slice(1).toLowerCase()}
                    </Pill>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((current) => current - 1)}
            className="rounded-control border border-ink/15 px-3 py-1.5 font-semibold text-ink disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-ink-muted">
            Page {page} of {pages}
          </span>
          <button
            type="button"
            disabled={page >= pages}
            onClick={() => setPage((current) => current + 1)}
            className="rounded-control border border-ink/15 px-3 py-1.5 font-semibold text-ink disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </>
  );
}
